import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';

export interface SearchableListSelectProps<T> {
  /** Pre-loaded list of items to choose from. */
  items: T[];
  /** Selected value (id) or array of ids when `multiple`. */
  value: string | string[] | null;
  /** Called with the next value (string for single, string[] for multiple). */
  onChange: (value: string | string[]) => void;
  /** Extract the unique id used for `value`. */
  getValue: (item: T) => string;
  /** Extract the display label. */
  getLabel: (item: T) => string;
  /** Optional secondary line under the label. */
  getSubtitle?: (item: T) => string | null | undefined;
  /** Optional leading icon / color swatch / etc rendered inside each row. */
  renderItem?: (item: T, isSelected: boolean) => ReactNode;
  /** Override the default substring filter (case-insensitive on label + subtitle). */
  filterFn?: (item: T, query: string) => boolean;
  placeholder?: string;
  /** Multi-select mode renders chips and allows multiple selections. */
  multiple?: boolean;
  disabled?: boolean;
  /** Show an `X` button to clear the entire selection. Defaults to true. */
  clearable?: boolean;
  /** Loading state for async-loaded `items`. */
  isLoading?: boolean;
  /** Message shown when there are no items at all. */
  emptyMessage?: string;
  /** Message shown when items exist but none match the query. */
  noMatchMessage?: string;
  /** ARIA label for screen readers. */
  ariaLabel?: string;
  /** Add additional classes on the input element. */
  inputClassName?: string;
  /** Id for the input, so a `<label htmlFor>` names it. */
  inputId?: string;
}

function defaultFilter<T>(
  item: T,
  query: string,
  getLabel: (item: T) => string,
  getSubtitle?: (item: T) => string | null | undefined,
): boolean {
  const q = query.toLowerCase();
  if (getLabel(item).toLowerCase().includes(q)) return true;
  const sub = getSubtitle?.(item);
  if (sub && sub.toLowerCase().includes(q)) return true;
  return false;
}

export function SearchableListSelect<T>({
  items,
  value,
  onChange,
  getValue,
  getLabel,
  getSubtitle,
  renderItem,
  filterFn,
  placeholder = 'Select...',
  multiple = false,
  disabled = false,
  clearable = true,
  isLoading = false,
  emptyMessage = 'No options',
  noMatchMessage = 'No results found',
  ariaLabel,
  inputClassName,
  inputId,
}: SearchableListSelectProps<T>) {
  const listId = useId();
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Disabled while its list is open (a form locked for a save) closes it, so
  // no option can still be chosen.
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Selected ids ─────────────────
  const selectedIds = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    if (typeof value === 'string' && value) return [value];
    return [];
  }, [multiple, value]);

  const itemsById = useMemo(() => {
    const map = new Map<string, T>();
    for (const item of items) map.set(getValue(item), item);
    return map;
  }, [items, getValue]);

  const hasValue = selectedIds.length > 0;

  // ─── Filter ──────────────────────
  const filtered = useMemo(() => {
    const query = inputValue.trim();
    if (!query) return items;
    if (filterFn) return items.filter((item) => filterFn(item, query));
    return items.filter((item) => defaultFilter(item, query, getLabel, getSubtitle));
  }, [items, inputValue, filterFn, getLabel, getSubtitle]);

  // Keep highlightedIndex within range as filter changes.
  useEffect(() => {
    if (highlightedIndex >= filtered.length) setHighlightedIndex(filtered.length - 1);
  }, [filtered.length, highlightedIndex]);

  // ─── Handlers ────────────────────
  const handleSelect = useCallback(
    (item: T) => {
      const id = getValue(item);
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        if (current.includes(id)) {
          onChange(current.filter((v) => v !== id));
        } else {
          onChange([...current, id]);
        }
        setInputValue('');
        // keep open in multi-select mode
      } else {
        onChange(id);
        setInputValue('');
        setIsOpen(false);
      }
      setHighlightedIndex(-1);
    },
    [getValue, multiple, value, onChange],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        onChange(current.filter((v) => v !== id));
      } else {
        onChange('');
      }
    },
    [multiple, value, onChange],
  );

  const handleClear = useCallback(() => {
    onChange(multiple ? [] : '');
    setInputValue('');
    setHighlightedIndex(-1);
  }, [multiple, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        // An open list takes the Escape: it closes the list, not the dialog
        // the field sits in (modals listen for Escape on the window).
        if (isOpen) e.stopPropagation();
        setIsOpen(false);
        setHighlightedIndex(-1);
        return;
      }
      if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        setIsOpen(true);
        return;
      }
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          handleSelect(filtered[highlightedIndex]);
        }
      } else if (e.key === 'Backspace' && multiple && !inputValue && selectedIds.length > 0) {
        // Pop last chip on backspace when input is empty.
        handleRemove(selectedIds[selectedIds.length - 1]);
      }
    },
    [
      isOpen,
      filtered,
      highlightedIndex,
      handleSelect,
      multiple,
      inputValue,
      selectedIds,
      handleRemove,
    ],
  );

  // Close on outside click.
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view.
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('li[role="option"]');
      const el = items[highlightedIndex] as HTMLElement | undefined;
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // ─── Display label for single select ─
  const singleSelectedLabel = useMemo(() => {
    if (multiple || !selectedIds[0]) return null;
    const item = itemsById.get(selectedIds[0]);
    return item ? getLabel(item) : selectedIds[0];
  }, [multiple, selectedIds, itemsById, getLabel]);

  return (
    <div ref={containerRef} className="relative">
      {/* Multi-select chips */}
      {multiple && hasValue && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selectedIds.map((id) => {
            const item = itemsById.get(id);
            const label = item ? getLabel(item) : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full"
              >
                {label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(id)}
                    className="hover:text-primary-900"
                    aria-label={`Remove ${label}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={isOpen || multiple ? inputValue : (singleSelectedLabel ?? inputValue)}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={!multiple && singleSelectedLabel ? singleSelectedLabel : placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-16 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${inputClassName ?? ''}`}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-label={ariaLabel ?? placeholder}
          autoComplete="off"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && <Loader2 size={14} className="animate-spin text-gray-400" />}
          {clearable && hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={14}
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <ul
          id={listId}
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {items.length === 0 && !isLoading && (
            <li className="px-3 py-2 text-sm text-gray-500" role="status">
              {emptyMessage}
            </li>
          )}
          {items.length > 0 && filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500" role="status">
              {noMatchMessage}
            </li>
          )}
          {filtered.map((item, index) => {
            const id = getValue(item);
            const isSelected = selectedIds.includes(id);
            const isHighlighted = highlightedIndex === index;
            return (
              <li
                key={id}
                role="option"
                aria-selected={isSelected}
                data-highlighted={isHighlighted}
                className={`px-3 py-2 cursor-pointer text-sm ${
                  isHighlighted
                    ? 'bg-primary-50 text-primary-900'
                    : isSelected
                      ? 'bg-gray-50 text-gray-700'
                      : 'hover:bg-gray-50 text-gray-900'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {renderItem ? (
                  renderItem(item, isSelected)
                ) : (
                  <>
                    <div className="font-medium">{getLabel(item)}</div>
                    {getSubtitle && getSubtitle(item) && (
                      <div className="text-xs text-gray-500">{getSubtitle(item)}</div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
